import useCMS from '@telescope/react-hooks/useCMS';
import { css } from 'aphrodite/no-important';
import { Footer, LoadingSpinner, Nav, ProtectedRoute } from 'components';
import { ChatAvailableModal } from 'components/ChatAvailableModal/ChatAvailableModal';
import { SurveyAvailableModal } from 'components/SurveyAvailableModal/SurveyAvailableModal';
import { usePageViews } from 'hooks';
import useCustomIdleTimer, { CustomIdleTimerContext } from 'hooks/useCustomIdleTimer';
import isEmpty from 'lodash/isEmpty';
import { CmsData, LoginData } from 'models';
import { Achievements, Agenda, Closed, Exhibit, Home, InnovationZone, PartnerPavilion, Session, Survey } from 'pages';
import { TechnicalTracks } from 'pages/TechnicalTracks/TechnicalTracks';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { resetAchievements } from 'redux/reducers/achievements';
import { isExhibitorUser, setUserData } from 'redux/reducers/user';
import { updateVendors } from 'redux/reducers/vendors';
import { setMainWidgetData } from 'redux/reducers/widget';
import { RootState } from 'redux/store';
import api, { VOTE_API_ACTION_TYPE } from 'util/api';
import { DEFAULT_WID, MIN_POLLING_RATE, ROUTE } from 'util/constants';
import * as googleHelpers from 'util/google-helpers';
import { checkIfTrue, getQueryParamByName } from 'util/helpers';
import { readFromStorage, removeFromStorage, writeToStorage } from 'util/storage';
import { setSurveyHasBeenSubmittedByUser } from 'util/survey-helpers';
import vehApi from 'util/veh-api';
import { style } from './style';

export interface LocationState {
  from: {
    pathname: string;
  };
}

export const App = () => {
  const userData = useSelector((state: RootState) => state.user.data);
  const vendorState = useSelector((state: RootState) => state.vendors);
  const isExhibitor = useSelector(isExhibitorUser);
  const [storageKey, setStorageKey] = useState(null);
  const [isUserDataCheckComplete, setIsUserDataCheckComplete] = useState(false);
  const [hasLoggedInFromStorage, setHasLoggedInFromStorage] = useState(false);
  const location = useLocation<LocationState>();
  const staticNavPages = [ROUTE.HOME, ROUTE.CLOSED] as string[]; // pages that don't show all nav links
  const isNavStatic = staticNavPages.includes(location.pathname);
  const isHomePage = ROUTE.HOME === location.pathname;
  const wid = getQueryParamByName('wid') || (window as any).WID || DEFAULT_WID;
  const sid = getQueryParamByName('sid');

  const [cms] = useCMS<CmsData>(wid, { sid, pollingFrequency: MIN_POLLING_RATE });
  const dispatch = useDispatch();

  const styles = style({ isNavStatic, isHomePage });

  useEffect(() => {
    async function getVendors() {
      try {
        const vendors = await vehApi.getVendors(cms.settings.virtual_event_hash, cms.settings.exhibit_segment);
        dispatch(updateVendors(vendors));
      } catch {}
    }

    if (!vendorState.status && cms) {
      getVendors();
    }
  }, [cms, dispatch, vendorState.status]);

  const handleUserLogout = useCallback(
    async function () {
      if (userData && cms) {
        const { apiKey, version_id } = cms.settings;
        const { code, token } = userData;

        const payload = {
          apiKey,
          timestamp: Date.now(),
          token,
          code,
          action_type: VOTE_API_ACTION_TYPE.LOGOUT,
        };

        await api.postToVoteApi(payload, null, version_id);
      }

      removeFromStorage(storageKey);
      dispatch(setUserData(null));
      dispatch(resetAchievements());
      setHasLoggedInFromStorage(false);
    },
    [cms, dispatch, storageKey, userData]
  );

  const { resetIdleTimer } = useCustomIdleTimer({
    hasLoggedInFromStorage,
    onIdle: handleUserLogout,
  });

  const onActivity = useCallback(() => {
    resetIdleTimer();
  }, [resetIdleTimer]);

  // Log page views in GA
  usePageViews();

  // Sets User to be tracked in GA
  useEffect(() => {
    if (userData) {
      googleHelpers.setGoogleUser(userData.code, userData.company_name);
    }
  }, [userData]);

  // Save the new storage/session key to state
  useEffect(() => {
    if (!isEmpty(cms)) {
      const newStorageKey = cms.settings.session_id;
      setStorageKey(newStorageKey);
    }

    if (cms && !googleHelpers.isInitialized()) {
      googleHelpers.initializeGoogleAnalytics(cms.settings.google_analytics);
    }
  }, [cms]);

  // Reads, Sets, or clears userData
  useEffect(() => {
    if (!isEmpty(cms) && storageKey) {
      const newStorageKey = cms.settings.session_id;
      const storageKeyChanged = storageKey && newStorageKey !== storageKey;

      if (userData) {
        if (storageKeyChanged) {
          handleUserLogout();
        } else {
          writeToStorage(storageKey, userData);
        }
      } else {
        const storedUserData = readFromStorage(storageKey);
        dispatch(setUserData(storedUserData));
        setIsUserDataCheckComplete(true); // Indicates that we've completed the check against local storage
        setHasLoggedInFromStorage(storedUserData ? true : false); // Indicates that we've logged in using localStorage
      }
    }
  }, [cms, handleUserLogout, storageKey, userData, dispatch]);

  // Fetch CMS on app mount
  useEffect(() => {
    dispatch(setMainWidgetData(cms));
  }, [cms, dispatch]);

  /* When the user logs in, set the userData, and
   * set a flag in localStorage if the user has completed the survey
   */
  function handleUserLogin(loginData: LoginData) {
    const { isSurveyCompleted, userData } = loginData;
    if (isSurveyCompleted) {
      setSurveyHasBeenSubmittedByUser(userData);
    }
    dispatch(setUserData(userData));
  }

  if (!cms || !isUserDataCheckComplete) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  const isWindowClosed = !checkIfTrue(cms.settings.window_status);
  const isSurveyClosed = checkIfTrue(cms.text.survey.hide_page) || isExhibitor;

  return (
    <div className={css(styles.appContainer)}>
      <CustomIdleTimerContext.Provider value={onActivity}>
        {!isWindowClosed && <Nav onLogout={handleUserLogout} windowStatus={!isWindowClosed} />}
        <Switch>
          <ProtectedRoute redirectToClosed={isWindowClosed} path={ROUTE.PLENARY_SESSION}>
            <Session text={cms.text.plenary_session} route={ROUTE.PLENARY_SESSION} />
          </ProtectedRoute>
          <ProtectedRoute redirectToClosed={isWindowClosed} path={ROUTE.TECHNICAL_SESSION}>
            <Session text={cms.text.technical_session} route={ROUTE.TECHNICAL_SESSION} />
          </ProtectedRoute>
          <ProtectedRoute redirectToClosed={isWindowClosed} exact path={ROUTE.PARTNER_PAVILION}>
            <PartnerPavilion isOpen={checkIfTrue(cms.text.partner_pavilion.is_open)}></PartnerPavilion>
          </ProtectedRoute>
          <ProtectedRoute redirectToClosed={isWindowClosed} path={ROUTE.INNOVATION_ZONE}>
            <InnovationZone />
          </ProtectedRoute>
          <ProtectedRoute redirectToClosed={isWindowClosed} path={ROUTE.TECHNICAL_TRACKS}>
            <TechnicalTracks />
          </ProtectedRoute>
          <ProtectedRoute redirectToClosed={isWindowClosed} path={ROUTE.PARTNER_DETAIL}>
            <Exhibit />
          </ProtectedRoute>
          <ProtectedRoute
            redirectToClosed={isWindowClosed}
            conditionalRedirection={{ condition: isSurveyClosed, route: ROUTE.PLENARY_SESSION }}
            path={ROUTE.SURVEY}>
            <Survey />
          </ProtectedRoute>
          <ProtectedRoute redirectToClosed={isWindowClosed} path={ROUTE.ACHIEVEMENTS}>
            <Achievements />
          </ProtectedRoute>
          <ProtectedRoute redirectToClosed={isWindowClosed} path={ROUTE.AGENDA}>
            <Agenda />
          </ProtectedRoute>

          {isWindowClosed && (
            <Route exact path={ROUTE.CLOSED}>
              <Closed />
            </Route>
          )}

          <Redirect from={ROUTE.CLOSED} exact to={ROUTE.HOME} />

          <ProtectedRoute
            redirectToClosed={isWindowClosed}
            exact
            path={ROUTE.HOME}
            userData={userData}
            requiresAuth={false}>
            {userData ? (
              <Redirect to={cms.text.settings.redirect_after_login} />
            ) : (
              <Home cmsData={cms.text} onLogin={handleUserLogin} settings={cms.settings}></Home>
            )}
          </ProtectedRoute>
        </Switch>

        {!isWindowClosed && <ChatAvailableModal />}
        {!isWindowClosed && <SurveyAvailableModal />}
        <Footer cmsData={cms.text.footer} isWindowClosed={isWindowClosed}></Footer>
      </CustomIdleTimerContext.Provider>
    </div>
  );
};

export default App;
